/**
 * connectionManager — the single owner of the app-wide AppSync Events
 * notifications socket (PRP-PLAT-026 Task 6 / GW-8887, FR-1;
 * `gw-ui/docs/design/notifications-display-spec.md` §1.1/§3.3).
 *
 * gw-spa owns the connect/subscribe/reconnect lifecycle for the notifications
 * transport; the published client (`@gw-notifications/appsync-events-client`
 * >=0.2.0) owns wire decoding and hands us decoded `NotificationEvent`s. This
 * module is the seam between the two: it opens exactly ONE connection for the
 * signed-in user and forwards every decoded event to `ingestWireNotification`
 * (the GW-8886 store seam), which adapts + toasts.
 *
 * Design constraints (all load-bearing — see the issue and the spec):
 *
 * 1. **Exactly one connection app-wide.** All state is module-level; a second
 *    `startNotificationsConnection()` while one is live (or connecting) is a
 *    no-op. This is what makes StrictMode's deliberate double-invoke and HMR
 *    remounts safe.
 *
 * 2. **Spread-compose Amplify config — never wholesale-replace.** Amplify v6's
 *    `Amplify.configure` REPLACES the entire config object; it does not merge.
 *    The client's own `configureAppSyncEvents()` calls a bare
 *    `Amplify.configure({ API: { Events } })`, which would wipe the Cognito
 *    `Auth` block that `awsCognito.util`'s `ensureAmplifyConfigured()` set —
 *    breaking auth. So we do NOT call the client's configure helper. Instead we
 *    run `ensureAmplifyConfigured()` first, then
 *    `Amplify.configure({ ...Amplify.getConfig(), API: { Events } })`,
 *    preserving everything already configured. (The client's "idempotent"
 *    docstring is wrong for our multi-config app — do not rely on it.)
 *
 * 3. **Connect with `userId` OMITTED and no `authToken`.** `connectToUser()`
 *    derives the Cognito `sub` from the signed-in Amplify session. We never
 *    pass a userId (from props/URL/store) and never use the `authToken` option
 *    (it has an open Amplify 6.19 header-forwarding bug, and pasting a token is
 *    a dev-only fallback we deliberately do not take).
 *
 * 4. **Capped + jittered exponential backoff on `onError`, retrying at the
 *    ceiling indefinitely.** The client does NOT auto-reconnect. Each error
 *    schedules a reconnect after `min(BASE * 2^attempt, MAX)` with equal-jitter
 *    (half fixed, half random) so a fleet of clients does not reconnect in
 *    lockstep. Attempts keep going forever once at the ceiling — a socket that
 *    stays down must keep trying.
 *
 * 5. **Cancellation for StrictMode/HMR double-mount.** A monotonically
 *    increasing generation counter tags every start. Any async continuation
 *    (a resolving `connectToUser`, a firing backoff timer) checks whether its
 *    generation is still current and bails if `stop()` (or a newer start) has
 *    superseded it. The pending backoff timer is always cleared on stop.
 *
 * 6. **No token/payload logging anywhere, including error paths.** This module
 *    NEVER passes a wire event, an error object, or any connection detail to
 *    `console.*` or any telemetry sink. Errors are handled structurally
 *    (schedule a reconnect); their contents are never read or emitted. The one
 *    thing an error influences is the backoff delay — a number, not content.
 */
import { Amplify } from 'aws-amplify';
import {
  connectToUser,
  type NotificationEvent,
  type UserConnection,
} from '@gw-notifications/appsync-events-client';

import { getConfig } from '@/config/runtimeConfig';
import { ensureAmplifyConfigured } from '@/lib/awsCognito.util';
import { ingestWireNotification } from '@/lib/notifications/notificationsSlice';
import type { WireNotificationEvent } from '@/lib/notifications/toNotificationRecord';

/** Backoff base delay (ms) for the first reconnect attempt. */
const BACKOFF_BASE_MS = 1_000;
/** Backoff ceiling (ms) — attempts cap here and retry indefinitely. */
const BACKOFF_MAX_MS = 30_000;

interface ManagerState {
  /** The current generation. Bumped on every start() and every stop(). */
  generation: number;
  /** True while a connection is live or a connect/reconnect is in flight. */
  active: boolean;
  /** The live connection, once `connectToUser` resolves. */
  connection: UserConnection | null;
  /** Pending backoff timer handle, so stop() can cancel it. */
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  /** Consecutive-failure counter driving the exponential backoff. */
  attempt: number;
}

const state: ManagerState = {
  generation: 0,
  active: false,
  connection: null,
  reconnectTimer: null,
  attempt: 0,
};

/**
 * Equal-jitter capped exponential backoff: `half = cap/2`, delay =
 * `half + random(0, half)` where `cap = min(BASE * 2^attempt, MAX)`. Keeps a
 * floor (never 0ms) while spreading reconnects across the fleet.
 */
function backoffDelayMs(attempt: number): number {
  const cap = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS);
  const half = cap / 2;
  return half + Math.random() * half;
}

/**
 * Composes the Amplify config for the AppSync Events transport WITHOUT
 * discarding whatever is already configured (Auth.Cognito, existing API).
 * Runs `ensureAmplifyConfigured()` first so the Cognito block exists to spread.
 */
function configureEventsTransport(endpoint: string, region: string): void {
  ensureAmplifyConfigured();
  const existing = Amplify.getConfig();
  Amplify.configure({
    ...existing,
    API: {
      ...existing.API,
      Events: { endpoint, region, defaultAuthMode: 'userPool' },
    },
  });
}

/** Forwards a decoded wire event into the store/toast ingest seam. */
function handleEvent(event: NotificationEvent): void {
  // The 0.2.0 client's NotificationEvent (id/type/emittedAt/userId/…) is
  // structurally a WireNotificationEvent; ingest adapts + toasts.
  ingestWireNotification(event as WireNotificationEvent);
}

/**
 * Opens one connection for the current generation and wires its handlers.
 * Any error (rejected connect, or a later `onError`) schedules a backoff
 * reconnect tagged with the same generation.
 */
async function openConnection(generation: number): Promise<void> {
  let connection: UserConnection;
  try {
    // userId omitted (client derives the Cognito sub); authToken never set.
    connection = await connectToUser();
  } catch {
    // Structural handling only — never read/emit the error.
    scheduleReconnect(generation);
    return;
  }

  // A stop() (or newer start) may have superseded us while awaiting connect.
  if (generation !== state.generation || !state.active) {
    void connection.close();
    return;
  }

  state.connection = connection;
  state.attempt = 0;

  connection.onEvent((event) => {
    if (generation === state.generation) {
      handleEvent(event);
    }
  });

  connection.onError(() => {
    // Drop the dead connection and reconnect with backoff. The error's
    // contents are never inspected or logged.
    if (generation !== state.generation) {
      return;
    }
    void connection.close();
    if (state.connection === connection) {
      state.connection = null;
    }
    scheduleReconnect(generation);
  });
}

/** Schedules a reconnect after a jittered backoff, unless superseded. */
function scheduleReconnect(generation: number): void {
  if (generation !== state.generation || !state.active) {
    return;
  }
  clearReconnectTimer();
  const delay = backoffDelayMs(state.attempt);
  state.attempt += 1;
  state.reconnectTimer = setTimeout(() => {
    state.reconnectTimer = null;
    if (generation !== state.generation || !state.active) {
      return;
    }
    void openConnection(generation);
  }, delay);
}

function clearReconnectTimer(): void {
  if (state.reconnectTimer !== null) {
    clearTimeout(state.reconnectTimer);
    state.reconnectTimer = null;
  }
}

/**
 * Starts the single app-wide notifications connection. No-op if one is already
 * active (duplicate mounts) or if the AppSync Events transport is not
 * configured for this environment (`notificationsAppSync` is null → inert).
 */
export async function startNotificationsConnection(): Promise<void> {
  if (state.active) {
    return;
  }

  const { notificationsAppSync } = getConfig();
  if (!notificationsAppSync) {
    // No transport provisioned in this environment — stay inert.
    return;
  }

  state.generation += 1;
  state.active = true;
  state.attempt = 0;
  const generation = state.generation;

  configureEventsTransport(notificationsAppSync.endpoint, notificationsAppSync.region);
  await openConnection(generation);
}

/**
 * Stops the connection: cancels any pending reconnect, closes the live
 * connection, and bumps the generation so any in-flight async continuation
 * bails. Safe to call more than once and when never started.
 */
export async function stopNotificationsConnection(): Promise<void> {
  state.active = false;
  state.generation += 1;
  state.attempt = 0;
  clearReconnectTimer();

  const connection = state.connection;
  state.connection = null;
  if (connection) {
    await connection.close();
  }
}

/** True when a live connection is currently held. */
export function isNotificationsConnected(): boolean {
  return state.active && state.connection !== null;
}
